import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API, BACKEND_URL } from '@/App';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Trash2, Instagram, BookOpen, Image, Quote, Users, FolderPlus, Folder, ArrowLeft, Camera, ImageIcon, Download, X, ZoomIn, ChevronLeft, ChevronRight, Server, FolderInput, Loader2, Shield } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import BuenasPracticasTab from '@/components/BuenasPracticasTab';

function Galeria() {
  const [photos, setPhotos] = useState([]);
  const [mkPhotos, setMkPhotos] = useState([]);
  const [stoicQuotes, setStoicQuotes] = useState([]);
  const [folders, setFolders] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [mkDialogOpen, setMkDialogOpen] = useState(false);
  const [quoteDialogOpen, setQuoteDialogOpen] = useState(false);
  const [folderDialogOpen, setFolderDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [lightboxImages, setLightboxImages] = useState([]);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const { user } = useAuth();
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [previewUrls, setPreviewUrls] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [mkSelectedFiles, setMkSelectedFiles] = useState([]);
  const [mkPreviewUrls, setMkPreviewUrls] = useState([]);
  const [mkUploading, setMkUploading] = useState(false);
  const [mkUploadProgress, setMkUploadProgress] = useState(0);
  const [formData, setFormData] = useState({
    titulo: '',
    descripcion: '',
    carpeta: ''
  });
  const [mkFormData, setMkFormData] = useState({
    titulo: '',
    descripcion: ''
  });
  const [quoteFormData, setQuoteFormData] = useState({
    frase: '',
    autor: ''
  });
  const [quoteImageFile, setQuoteImageFile] = useState(null);
  const [quoteImagePreview, setQuoteImagePreview] = useState(null);
  const [uploadingQuote, setUploadingQuote] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [creatingFolder, setCreatingFolder] = useState(false);
  
  // Estados para importación desde servidor
  const [serverFolders, setServerFolders] = useState(null);
  const [loadingServerFolders, setLoadingServerFolders] = useState(false);
  const [importPath, setImportPath] = useState('');
  const [importName, setImportName] = useState('');
  const [importing, setImporting] = useState(false);
  
  // Estados para carpetas externas (vinculadas del servidor)
  const [externalFolders, setExternalFolders] = useState([]);
  const [selectedExternalFolder, setSelectedExternalFolder] = useState(null);
  const [externalImages, setExternalImages] = useState([]);
  const [loadingExternalImages, setLoadingExternalImages] = useState(false);

  // Funciones para lightbox
  const openLightbox = (imageUrl, title, imagesArray = [], index = 0) => {
    setLightboxImage({ url: imageUrl, title: title || 'Imagen' });
    setLightboxImages(imagesArray);
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    setLightboxImage(null);
    setLightboxImages([]);
    setLightboxIndex(0);
  };

  const navigateLightbox = (direction) => {
    if (lightboxImages.length === 0) return;
    
    let newIndex;
    if (direction === 'prev') {
      newIndex = lightboxIndex > 0 ? lightboxIndex - 1 : lightboxImages.length - 1;
    } else {
      newIndex = lightboxIndex < lightboxImages.length - 1 ? lightboxIndex + 1 : 0;
    }
    
    setLightboxIndex(newIndex);
    const newImage = lightboxImages[newIndex];
    setLightboxImage({ url: newImage.url, title: newImage.title || 'Imagen' });
  };

  // Manejar teclas de navegación
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!lightboxOpen) return;
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowLeft' && lightboxImages.length > 0) {
        const newIndex = lightboxIndex > 0 ? lightboxIndex - 1 : lightboxImages.length - 1;
        setLightboxIndex(newIndex);
        setLightboxImage({ url: lightboxImages[newIndex].url, title: lightboxImages[newIndex].title || 'Imagen' });
      }
      if (e.key === 'ArrowRight' && lightboxImages.length > 0) {
        const newIndex = lightboxIndex < lightboxImages.length - 1 ? lightboxIndex + 1 : 0;
        setLightboxIndex(newIndex);
        setLightboxImage({ url: lightboxImages[newIndex].url, title: lightboxImages[newIndex].title || 'Imagen' });
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxOpen, lightboxIndex, lightboxImages]);

  const downloadImage = async () => {
    if (!lightboxImage) return;
    try {
      const response = await fetch(lightboxImage.url);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${lightboxImage.title || 'imagen'}.jpg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      // Fallback: abrir en nueva pestaña
      window.open(lightboxImage.url, '_blank');
    }
  };

  useEffect(() => {
    fetchPhotos();
    fetchMkPhotos();
    fetchStoicQuotes();
    fetchFolders();
    fetchExternalFolders();
  }, []);

  const fetchPhotos = async () => {
    try {
      const response = await axios.get(`${API}/photos`);
      setPhotos(response.data.filter(p => p.categoria !== 'mk'));
    } catch (error) {
      console.error('Error al cargar fotos:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMkPhotos = async () => {
    try {
      const response = await axios.get(`${API}/photos/mk`);
      setMkPhotos(response.data);
    } catch (error) {
      console.error('Error al cargar fotos MK:', error);
    }
  };

  const fetchStoicQuotes = async () => {
    try {
      const response = await axios.get(`${API}/stoic-quotes`);
      setStoicQuotes(response.data);
    } catch (error) {
      console.error('Error al cargar frases:', error);
    }
  };

  const fetchFolders = async () => {
    try {
      const response = await axios.get(`${API}/photos/folders`);
      setFolders(response.data);
    } catch (error) {
      console.error('Error al cargar carpetas:', error);
    }
  };

  // Funciones para importar desde servidor
  const fetchServerFolders = async (ruta = '/external_photos') => {
    setLoadingServerFolders(true);
    try {
      const response = await axios.get(`${API}/photos/browse-server?ruta=${encodeURIComponent(ruta)}`);
      setServerFolders(response.data);
    } catch (error) {
      console.error('Error al cargar carpetas del servidor:', error);
      setServerFolders({ existe: false, mensaje: error.response?.data?.detail || 'Error al conectar' });
    } finally {
      setLoadingServerFolders(false);
    }
  };

  // Funciones para carpetas externas vinculadas
  const fetchExternalFolders = async () => {
    try {
      const response = await axios.get(`${API}/photos/external-folders`);
      setExternalFolders(response.data);
    } catch (error) {
      console.error('Error al cargar carpetas externas:', error);
    }
  };

  const fetchExternalImages = async (folderId) => {
    setLoadingExternalImages(true);
    try {
      const response = await axios.get(`${API}/photos/external-folder/${folderId}/images`);
      setExternalImages(response.data.images || []);
    } catch (error) {
      console.error('Error al cargar imágenes externas:', error);
      alert('Error al cargar imágenes: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoadingExternalImages(false);
    }
  };

  const handleAddExternalFolder = async (e) => {
    e.preventDefault();
    if (!importPath.trim() || !importName.trim()) {
      alert('Por favor completa la ruta y el nombre de la carpeta');
      return;
    }
    
    setImporting(true);
    try {
      const response = await axios.post(`${API}/photos/external-folder`, {
        nombre: importName,
        ruta_servidor: importPath
      });
      
      fetchExternalFolders();
      setImportPath('');
      setImportName('');
      setImportDialogOpen(false);
      
      alert(`✅ Carpeta vinculada!\n\n${response.data.nombre}\n${response.data.imagenes} imágenes encontradas`);
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setImporting(false);
    }
  };

  const handleDeleteExternalFolder = async (folderId, folderName) => {
    if (window.confirm(`¿Desvincular la carpeta "${folderName}"?\n\n(Los archivos originales NO se borrarán)`)) {
      try {
        await axios.delete(`${API}/photos/external-folder/${folderId}`);
        fetchExternalFolders();
        if (selectedExternalFolder === folderId) {
          setSelectedExternalFolder(null);
          setExternalImages([]);
        }
        alert('Carpeta desvinculada');
      } catch (error) {
        alert('Error: ' + (error.response?.data?.detail || error.message));
      }
    }
  };

  const handleImportFromServer = async (e) => {
    e.preventDefault();
    // Usar handleAddExternalFolder en lugar de importar y copiar
    await handleAddExternalFolder(e);
  };

  const handleCreateFolder = async (e) => {
    e.preventDefault();
    if (!newFolderName.trim()) {
      alert('Por favor introduce un nombre para la carpeta');
      return;
    }
    
    setCreatingFolder(true);
    try {
      await axios.post(`${API}/photos/folders`, { nombre: newFolderName });
      fetchFolders();
      setNewFolderName('');
      setFolderDialogOpen(false);
      alert('Carpeta creada correctamente');
    } catch (error) {
      alert('Error al crear carpeta: ' + (error.response?.data?.detail || error.message));
    } finally {
      setCreatingFolder(false);
    }
  };

  const handleDeleteFolder = async (folderId, folderName) => {
    if (window.confirm(`¿Estás seguro de eliminar la carpeta "${folderName}" y todas sus fotos?`)) {
      try {
        await axios.delete(`${API}/photos/folders/${folderId}`);
        fetchFolders();
        fetchPhotos();
        setSelectedFolder(null);
      } catch (error) {
        alert('Error al eliminar carpeta');
      }
    }
  };

  // Función para abrir selector de galería (sin capture) - MÚLTIPLE
  const openGalleryPicker = (callback, multiple = false) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.multiple = multiple;
    input.onchange = (e) => callback(e);
    input.click();
  };

  // Función para abrir cámara (con capture)
  const openCameraPicker = (callback) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.capture = 'environment';
    input.onchange = (e) => callback(e);
    input.click();
  };

  // Manejar selección de múltiples archivos
  const handleMultipleFileChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      setSelectedFiles(files);
      const previews = [];
      files.forEach(file => {
        const reader = new FileReader();
        reader.onloadend = () => {
          previews.push(reader.result);
          if (previews.length === files.length) {
            setPreviewUrls([...previews]);
          }
        };
        reader.readAsDataURL(file);
      });
    }
  };

  // Manejar selección de archivo único (cámara)
  const handleSingleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFiles([file]);
      const reader = new FileReader();
      reader.onloadend = () => setPreviewUrls([reader.result]);
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (selectedFiles.length === 0) {
      alert('Por favor selecciona al menos una foto');
      return;
    }
    
    setUploading(true);
    setUploadProgress(0);
    
    // Usar selectedFolder si estamos dentro de una carpeta, sino usar formData.carpeta
    const carpetaDestino = selectedFolder || formData.carpeta || '';
    console.log('DEBUG Upload - selectedFolder:', selectedFolder, 'formData.carpeta:', formData.carpeta, 'carpetaDestino:', carpetaDestino);
    
    try {
      const totalFiles = selectedFiles.length;
      let uploaded = 0;
      
      for (const file of selectedFiles) {
        const formDataToSend = new FormData();
        formDataToSend.append('file', file);
        if (formData.titulo && totalFiles === 1) formDataToSend.append('titulo', formData.titulo);
        if (formData.descripcion && totalFiles === 1) formDataToSend.append('descripcion', formData.descripcion);
        if (carpetaDestino) formDataToSend.append('carpeta', carpetaDestino);
        console.log('DEBUG Upload - Sending carpeta:', carpetaDestino);
        
        await axios.post(`${API}/photos`, formDataToSend, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        uploaded++;
        setUploadProgress(Math.round((uploaded / totalFiles) * 100));
      }
      
      fetchPhotos();
      resetForm();
      setDialogOpen(false);
      alert(`${totalFiles} foto${totalFiles > 1 ? 's subidas' : ' subida'} correctamente`);
    } catch (error) {
      alert('Error al subir foto: ' + (error.response?.data?.detail || error.message));
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  // Funciones para Galería MK
  const handleMkFileChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      setMkSelectedFiles(files);
      const previews = [];
      files.forEach(file => {
        const reader = new FileReader();
        reader.onloadend = () => {
          previews.push(reader.result);
          if (previews.length === files.length) {
            setMkPreviewUrls([...previews]);
          }
        };
        reader.readAsDataURL(file);
      });
    }
  };

  const handleMkSubmit = async (e) => {
    e.preventDefault();
    
    if (mkSelectedFiles.length === 0) {
      alert('Por favor selecciona al menos una foto');
      return;
    }
    
    setMkUploading(true);
    setMkUploadProgress(0);
    
    try {
      const totalFiles = mkSelectedFiles.length;
      let uploaded = 0;
      
      for (const file of mkSelectedFiles) {
        const formDataToSend = new FormData();
        formDataToSend.append('file', file);
        formDataToSend.append('categoria', 'mk');
        if (mkFormData.titulo && totalFiles === 1) formDataToSend.append('titulo', mkFormData.titulo);
        if (mkFormData.descripcion && totalFiles === 1) formDataToSend.append('descripcion', mkFormData.descripcion);
        
        await axios.post(`${API}/photos`, formDataToSend, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        uploaded++;
        setMkUploadProgress(Math.round((uploaded / totalFiles) * 100));
      }
      
      fetchMkPhotos();
      resetMkForm();
      setMkDialogOpen(false);
      alert(`${totalFiles} foto${totalFiles > 1 ? 's MK subidas' : ' MK subida'} correctamente`);
    } catch (error) {
      alert('Error al subir foto: ' + (error.response?.data?.detail || error.message));
    } finally {
      setMkUploading(false);
      setMkUploadProgress(0);
    }
  };

  const handleDeleteMk = async (id) => {
    if (window.confirm('¿Estás seguro de eliminar esta foto?')) {
      try {
        await axios.delete(`${API}/photos/${id}`);
        fetchMkPhotos();
      } catch (error) {
        alert('Error al eliminar foto');
      }
    }
  };

  const resetMkForm = () => {
    setMkFormData({ titulo: '', descripcion: '' });
    setMkSelectedFiles([]);
    setMkPreviewUrls([]);
  };

  const handleQuoteImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setQuoteImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setQuoteImagePreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleQuoteSubmit = async (e) => {
    e.preventDefault();
    setUploadingQuote(true);
    
    try {
      let imagen_url = null;
      
      // Si hay imagen, subirla primero
      if (quoteImageFile) {
        const imageFormData = new FormData();
        imageFormData.append('file', quoteImageFile);
        imageFormData.append('titulo', `Estoico: ${quoteFormData.autor}`);
        
        const uploadRes = await axios.post(`${API}/photos`, imageFormData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        imagen_url = `${BACKEND_URL}${uploadRes.data.url}`;
      }
      
      await axios.post(`${API}/stoic-quotes`, {
        ...quoteFormData,
        imagen_url
      });
      
      fetchStoicQuotes();
      setQuoteFormData({ frase: '', autor: '' });
      setQuoteImageFile(null);
      setQuoteImagePreview(null);
      setQuoteDialogOpen(false);
      alert('Frase añadida correctamente');
    } catch (error) {
      alert('Error al añadir frase: ' + (error.response?.data?.detail || error.message));
    } finally {
      setUploadingQuote(false);
    }
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      setSelectedFiles(files);
      const previews = [];
      files.forEach(file => {
        const reader = new FileReader();
        reader.onloadend = () => {
          previews.push(reader.result);
          if (previews.length === files.length) {
            setPreviewUrls([...previews]);
          }
        };
        reader.readAsDataURL(file);
      });
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('¿Estás seguro de eliminar esta foto?')) {
      try {
        const response = await axios.delete(`${API}/photos/${id}`);
        console.log('Foto eliminada:', response.data);
        fetchPhotos();
      } catch (error) {
        console.error('Error al eliminar foto:', error.response?.data || error);
        alert('Error al eliminar foto: ' + (error.response?.data?.detail || error.message));
      }
    }
  };

  const handleDeleteQuote = async (id) => {
    if (window.confirm('¿Estás seguro de eliminar esta frase?')) {
      try {
        await axios.delete(`${API}/stoic-quotes/${id}`);
        fetchStoicQuotes();
      } catch (error) {
        alert('Error al eliminar frase');
      }
    }
  };

  const resetForm = () => {
    setFormData({ titulo: '', descripcion: '', carpeta: '' });
    setSelectedFiles([]);
    setPreviewUrls([]);
  };

  // Filtrar fotos por carpeta
  const getFilteredPhotos = () => {
    if (selectedFolder === null) {
      return photos.filter(p => !p.carpeta);
    }
    return photos.filter(p => p.carpeta === selectedFolder);
  };

  const selectedFolderData = folders.find(f => f.id === selectedFolder);

  return (
    <div className="min-h-screen bg-dark-mk">
      <Navbar />
      <div className="container mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold text-gold mb-2" data-testid="galeria-title">
              Galería
            </h1>
            <p className="text-gray-400">Fotos y frases de la comunidad</p>
          </div>
          <Button
            className="btn-primary-mk"
            onClick={() => window.open('https://www.instagram.com/mkmallorcaklan/', '_blank')}
          >
            <Instagram className="mr-2 h-4 w-4" /> Instagram
          </Button>
        </div>

        <Tabs defaultValue="fotos" className="w-full">
          <TabsList className="grid w-full max-w-2xl grid-cols-4 mb-8 bg-gray-800 gap-1">
            <TabsTrigger value="fotos" className="data-[state=active]:bg-gold data-[state=active]:text-gray-900 px-1 sm:px-3 text-[11px] sm:text-sm">
              <Image className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Fotos</span>
            </TabsTrigger>
            <TabsTrigger value="mk" className="data-[state=active]:bg-gold data-[state=active]:text-gray-900 px-1 sm:px-3 text-[11px] sm:text-sm">
              <Users className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Galería MK</span>
            </TabsTrigger>
            <TabsTrigger value="estoico" className="data-[state=active]:bg-gold data-[state=active]:text-gray-900 px-1 sm:px-3 text-[11px] sm:text-sm">
              <BookOpen className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Rincón Estoico</span>
            </TabsTrigger>
            <TabsTrigger value="normas" className="data-[state=active]:bg-gold data-[state=active]:text-gray-900 px-1 sm:px-3 text-[11px] sm:text-sm" data-testid="tab-normas">
              <Shield className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Normas</span>
            </TabsTrigger>
          </TabsList>

          {/* Tab de Fotos */}
          <TabsContent value="fotos">
            {/* Navegación de carpetas */}
            {selectedFolder !== null ? (
              <div className="mb-6">
                <Button 
                  variant="ghost" 
                  className="text-gold hover:text-yellow-400 mb-4"
                  onClick={() => setSelectedFolder(null)}
                >
                  <ArrowLeft className="mr-2 h-4 w-4" /> Volver a carpetas
                </Button>
                <div className="flex justify-between items-center">
                  <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                    <Folder className="h-6 w-6 text-gold" />
                    {selectedFolderData?.nombre || 'Carpeta'}
                  </h2>
                  <div className="flex gap-2">
                    <Dialog open={dialogOpen} onOpenChange={(open) => {
                      setDialogOpen(open);
                      if (!open) resetForm();
                      else {
                        console.log('DEBUG Dialog Open - selectedFolder:', selectedFolder);
                        setFormData({ ...formData, carpeta: selectedFolder });
                      }
                    }}>
                      <DialogTrigger asChild>
                        <Button className="btn-primary-mk">
                          <Plus className="mr-2 h-4 w-4" /> Subir Foto
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="bg-gray-900 text-white border-gray-700 max-h-[90vh] overflow-y-auto">
                        <DialogHeader>
                          <DialogTitle className="text-gold">Subir Fotos a &ldquo;{selectedFolderData?.nombre}&rdquo;</DialogTitle>
                        </DialogHeader>
                        <form onSubmit={handleSubmit} className="space-y-4">
                          <div className="space-y-2">
                            <Label>Seleccionar Fotos</Label>
                            <div className="flex gap-2">
                              <Button
                                type="button"
                                variant="outline"
                                className="flex-1 border-gray-600"
                                onClick={() => openGalleryPicker(handleMultipleFileChange, true)}
                              >
                                <ImageIcon className="mr-2 h-4 w-4" /> Galería (múltiple)
                              </Button>
                              <Button
                                type="button"
                                variant="outline"
                                className="flex-1 border-gray-600"
                                onClick={() => openCameraPicker(handleSingleFileChange)}
                              >
                                <Camera className="mr-2 h-4 w-4" /> Cámara
                              </Button>
                            </div>
                          </div>
                          {previewUrls.length > 0 && (
                            <div className="space-y-2">
                              <Label>{previewUrls.length} foto{previewUrls.length > 1 ? 's' : ''} seleccionada{previewUrls.length > 1 ? 's' : ''}</Label>
                              <div className="grid grid-cols-3 gap-2 max-h-48 overflow-y-auto">
                                {previewUrls.map((url, idx) => (
                                  <img key={idx} src={url} alt={`Preview ${idx + 1}`} className="w-full h-20 object-cover rounded" />
                                ))}
                              </div>
                            </div>
                          )}
                          {previewUrls.length === 1 && (
                            <>
                              <div className="space-y-2">
                                <Label htmlFor="titulo">Título (opcional)</Label>
                                <Input
                                  id="titulo"
                                  value={formData.titulo}
                                  onChange={(e) => setFormData({ ...formData, titulo: e.target.value })}
                                  className="bg-gray-800 border-gray-700 text-white"
                                />
                              </div>
                              <div className="space-y-2">
                                <Label htmlFor="descripcion">Descripción (opcional)</Label>
                                <Textarea
                                  id="descripcion"
                                  value={formData.descripcion}
                                  onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
                                  className="bg-gray-800 border-gray-700 text-white"
                                  rows={3}
                                />
                              </div>
                            </>
                          )}
                          {uploading && uploadProgress > 0 && (
                            <div className="w-full bg-gray-700 rounded-full h-2">
                              <div className="bg-gold h-2 rounded-full transition-all" style={{ width: `${uploadProgress}%` }}></div>
                            </div>
                          )}
                          <Button type="submit" className="w-full btn-primary-mk" disabled={uploading || selectedFiles.length === 0}>
                            {uploading ? `Subiendo... ${uploadProgress}%` : `Subir ${selectedFiles.length || ''} Foto${selectedFiles.length !== 1 ? 's' : ''}`}
                          </Button>
                        </form>
                      </DialogContent>
                    </Dialog>
                    {user?.role === 'entrenador' && (
                      <Button 
                        variant="destructive" 
                        size="icon"
                        onClick={() => handleDeleteFolder(selectedFolder, selectedFolderData?.nombre)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="mb-6">
                <div className="flex justify-end gap-2 mb-4">
                  {/* Botón Importar desde Servidor - Solo Admin */}
                  {user?.role === 'entrenador' && (
                    <Dialog open={importDialogOpen} onOpenChange={(open) => {
                      setImportDialogOpen(open);
                      if (open) fetchServerFolders();
                    }}>
                      <DialogTrigger asChild>
                        <Button variant="outline" className="border-gold text-gold hover:bg-gold hover:text-gray-900">
                          <Server className="mr-2 h-4 w-4" /> Importar desde Servidor
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="bg-gray-900 text-white border-gray-700 max-w-2xl max-h-[90vh] overflow-y-auto">
                        <DialogHeader>
                          <DialogTitle className="text-gold flex items-center gap-2">
                            <FolderInput className="h-5 w-5" /> Importar Carpeta desde Synology
                          </DialogTitle>
                        </DialogHeader>
                        
                        {loadingServerFolders ? (
                          <div className="flex items-center justify-center py-8">
                            <Loader2 className="h-8 w-8 animate-spin text-gold" />
                          </div>
                        ) : serverFolders?.existe === false ? (
                          <div className="space-y-4">
                            <div className="bg-red-900/30 border border-red-700 rounded-lg p-4">
                              <p className="text-red-400 font-medium mb-2">⚠️ Volumen no montado</p>
                              <p className="text-gray-400 text-sm">{serverFolders?.mensaje}</p>
                            </div>
                            <div className="bg-gray-800 rounded-lg p-4">
                              <p className="text-gray-300 text-sm mb-2">Añade esto a tu <code className="bg-gray-700 px-1 rounded">docker-compose.yml</code>:</p>
                              <pre className="bg-gray-950 p-3 rounded text-xs text-green-400 overflow-x-auto">
{`backend:
  volumes:
    - /volume1/fotos:/external_photos:ro`}
                              </pre>
                            </div>
                          </div>
                        ) : (
                          <div className="space-y-4">
                            <div className="bg-green-900/30 border border-green-700 rounded-lg p-3">
                              <p className="text-green-400 text-sm">✅ Conexión OK - Ruta: {serverFolders?.ruta_base}</p>
                              {serverFolders?.imagenes_en_raiz > 0 && (
                                <p className="text-gray-400 text-xs mt-1">{serverFolders.imagenes_en_raiz} imágenes en la raíz</p>
                              )}
                            </div>
                            
                            {serverFolders?.carpetas?.length > 0 && (
                              <div className="space-y-2">
                                <Label>Carpetas disponibles (clic para seleccionar):</Label>
                                <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto">
                                  {serverFolders.carpetas.map((folder) => (
                                    <button
                                      key={folder.ruta}
                                      type="button"
                                      onClick={() => {
                                        setImportPath(folder.ruta);
                                        setImportName(folder.nombre);
                                      }}
                                      className={`text-left p-2 rounded border transition-all ${
                                        importPath === folder.ruta 
                                          ? 'border-gold bg-gold/20' 
                                          : 'border-gray-700 hover:border-gray-500'
                                      }`}
                                    >
                                      <div className="flex items-center gap-2">
                                        <Folder className="h-4 w-4 text-gold" />
                                        <span className="text-sm truncate">{folder.nombre}</span>
                                      </div>
                                      <p className="text-xs text-gray-500 mt-1">{folder.imagenes} imágenes</p>
                                    </button>
                                  ))}
                                </div>
                              </div>
                            )}
                            
                            <form onSubmit={handleImportFromServer} className="space-y-4 pt-4 border-t border-gray-700">
                              <div className="space-y-2">
                                <Label htmlFor="importPath">Ruta en el servidor</Label>
                                <Input
                                  id="importPath"
                                  value={importPath}
                                  onChange={(e) => setImportPath(e.target.value)}
                                  placeholder="/external_photos/mi-carpeta"
                                  className="bg-gray-800 border-gray-700 text-white font-mono text-sm"
                                  required
                                />
                              </div>
                              <div className="space-y-2">
                                <Label htmlFor="importName">Nombre para la galería</Label>
                                <Input
                                  id="importName"
                                  value={importName}
                                  onChange={(e) => setImportName(e.target.value)}
                                  placeholder="Ej: Excursión Buñola 2024"
                                  className="bg-gray-800 border-gray-700 text-white"
                                  required
                                />
                              </div>
                              <Button type="submit" className="w-full btn-primary-mk" disabled={importing}>
                                {importing ? (
                                  <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Importando...
                                  </>
                                ) : (
                                  <>
                                    <FolderInput className="mr-2 h-4 w-4" /> Importar Carpeta
                                  </>
                                )}
                              </Button>
                            </form>
                          </div>
                        )}
                      </DialogContent>
                    </Dialog>
                  )}
                  
                  <Dialog open={folderDialogOpen} onOpenChange={setFolderDialogOpen}>
                    <DialogTrigger asChild>
                      <Button className="btn-primary-mk">
                        <FolderPlus className="mr-2 h-4 w-4" /> Nueva Carpeta
                      </Button>
                    </DialogTrigger>
                      <DialogContent className="bg-gray-900 text-white border-gray-700">
                        <DialogHeader>
                          <DialogTitle className="text-gold">Crear Nueva Carpeta</DialogTitle>
                        </DialogHeader>
                        <form onSubmit={handleCreateFolder} className="space-y-4">
                          <div className="space-y-2">
                            <Label htmlFor="folderName">Nombre de la carpeta</Label>
                            <Input
                              id="folderName"
                              value={newFolderName}
                              onChange={(e) => setNewFolderName(e.target.value)}
                              placeholder="Ej: Excursión Serra de Tramuntana"
                              className="bg-gray-800 border-gray-700 text-white"
                              required
                            />
                          </div>
                          <Button type="submit" className="w-full btn-primary-mk" disabled={creatingFolder}>
                            {creatingFolder ? 'Creando...' : 'Crear Carpeta'}
                          </Button>
                        </form>
                      </DialogContent>
                    </Dialog>
                  <Dialog open={dialogOpen} onOpenChange={(open) => {
                    setDialogOpen(open);
                    if (!open) resetForm();
                  }}>
                    <DialogTrigger asChild>
                      <Button className="btn-primary-mk">
                        <Plus className="mr-2 h-4 w-4" /> Subir Foto
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="bg-gray-900 text-white border-gray-700 max-h-[90vh] overflow-y-auto">
                      <DialogHeader>
                        <DialogTitle className="text-gold">Subir Nuevas Fotos</DialogTitle>
                      </DialogHeader>
                      <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="space-y-2">
                          <Label>Seleccionar Fotos</Label>
                          <div className="flex gap-2">
                            <Button
                              type="button"
                              variant="outline"
                              className="flex-1 border-gray-600"
                              onClick={() => openGalleryPicker(handleMultipleFileChange, true)}
                            >
                              <ImageIcon className="mr-2 h-4 w-4" /> Galería (múltiple)
                            </Button>
                            <Button
                              type="button"
                              variant="outline"
                              className="flex-1 border-gray-600"
                              onClick={() => openCameraPicker(handleSingleFileChange)}
                            >
                              <Camera className="mr-2 h-4 w-4" /> Cámara
                            </Button>
                          </div>
                        </div>
                        {previewUrls.length > 0 && (
                          <div className="space-y-2">
                            <Label>{previewUrls.length} foto{previewUrls.length > 1 ? 's' : ''} seleccionada{previewUrls.length > 1 ? 's' : ''}</Label>
                            <div className="grid grid-cols-3 gap-2 max-h-48 overflow-y-auto">
                              {previewUrls.map((url, idx) => (
                                <img key={idx} src={url} alt={`Preview ${idx + 1}`} className="w-full h-20 object-cover rounded" />
                              ))}
                            </div>
                          </div>
                        )}
                        {previewUrls.length === 1 && (
                          <>
                            <div className="space-y-2">
                              <Label htmlFor="titulo">Título (opcional)</Label>
                              <Input
                                id="titulo"
                                value={formData.titulo}
                                onChange={(e) => setFormData({ ...formData, titulo: e.target.value })}
                                className="bg-gray-800 border-gray-700 text-white"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="descripcion">Descripción (opcional)</Label>
                              <Textarea
                                id="descripcion"
                                value={formData.descripcion}
                                onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
                                className="bg-gray-800 border-gray-700 text-white"
                                rows={3}
                              />
                            </div>
                          </>
                        )}
                        {folders.length > 0 && (
                          <div className="space-y-2">
                            <Label htmlFor="carpeta">Carpeta (opcional)</Label>
                            <Select 
                              value={formData.carpeta || "none"} 
                              onValueChange={(value) => setFormData({ ...formData, carpeta: value === "none" ? "" : value })}
                            >
                              <SelectTrigger className="bg-gray-800 border-gray-700 text-white">
                                <SelectValue placeholder="Sin carpeta" />
                              </SelectTrigger>
                              <SelectContent className="bg-gray-800 border-gray-700 text-white">
                                <SelectItem value="none">Sin carpeta</SelectItem>
                                {folders.map((folder) => (
                                  <SelectItem key={folder.id} value={folder.id}>
                                    {folder.nombre}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        )}
                        {uploading && uploadProgress > 0 && (
                          <div className="w-full bg-gray-700 rounded-full h-2">
                            <div className="bg-gold h-2 rounded-full transition-all" style={{ width: `${uploadProgress}%` }}></div>
                          </div>
                        )}
                        <Button type="submit" className="w-full btn-primary-mk" disabled={uploading || selectedFiles.length === 0}>
                          {uploading ? `Subiendo... ${uploadProgress}%` : `Subir ${selectedFiles.length || ''} Foto${selectedFiles.length !== 1 ? 's' : ''}`}
                        </Button>
                      </form>
                    </DialogContent>
                  </Dialog>
                </div>

                {/* Lista de carpetas */}
                {folders.length > 0 && (
                  <div className="mb-8">
                    <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                      <Folder className="h-5 w-5 text-gold" /> Carpetas
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                      {folders.map((folder) => {
                        const folderPhotos = photos.filter(p => p.carpeta === folder.id);
                        const coverPhoto = folderPhotos[0];
                        return (
                          <div 
                            key={folder.id}
                            className="cursor-pointer group"
                            onClick={() => setSelectedFolder(folder.id)}
                          >
                            <div className="aspect-square bg-gray-800 rounded-lg overflow-hidden relative border-2 border-transparent group-hover:border-gold transition-all">
                              {coverPhoto ? (
                                <img 
                                  src={`${BACKEND_URL}${coverPhoto.url}`} 
                                  alt={folder.nombre}
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center">
                                  <Folder className="h-16 w-16 text-gray-600" />
                                </div>
                              )}
                              <div className="absolute inset-0 bg-black/50 flex items-end p-3">
                                <div>
                                  <p className="text-white font-semibold truncate">{folder.nombre}</p>
                                  <p className="text-gray-400 text-sm">{folderPhotos.length} fotos</p>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Carpetas Externas del Servidor */}
                {externalFolders.length > 0 && (
                  <div className="mb-8">
                    <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                      <Server className="h-5 w-5 text-gold" /> Carpetas del Servidor
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                      {externalFolders.map((folder) => (
                        <div 
                          key={folder.id}
                          className={`cursor-pointer group relative ${!folder.accesible ? 'opacity-50' : ''}`}
                          onClick={() => {
                            if (folder.accesible) {
                              setSelectedExternalFolder(folder.id);
                              fetchExternalImages(folder.id);
                            } else {
                              alert('Esta carpeta ya no es accesible. Verifica que el volumen está montado.');
                            }
                          }}
                        >
                          <div className={`aspect-square bg-gray-800 rounded-lg overflow-hidden relative border-2 ${selectedExternalFolder === folder.id ? 'border-gold' : 'border-transparent'} group-hover:border-gold transition-all`}>
                            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-800 to-gray-900">
                              <Server className="h-16 w-16 text-gold/50" />
                            </div>
                            <div className="absolute inset-0 bg-black/50 flex items-end p-3">
                              <div className="w-full">
                                <p className="text-white font-semibold truncate">{folder.nombre}</p>
                                <p className="text-gray-400 text-sm">{folder.imagenes} imágenes</p>
                                {!folder.accesible && (
                                  <p className="text-red-400 text-xs">No accesible</p>
                                )}
                              </div>
                            </div>
                            {user?.role === 'entrenador' && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteExternalFolder(folder.id, folder.nombre);
                                }}
                                className="absolute top-2 right-2 p-1 bg-red-600/80 hover:bg-red-600 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                              >
                                <Trash2 className="h-4 w-4 text-white" />
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Vista de imágenes de carpeta externa seleccionada */}
                {selectedExternalFolder && (
                  <div className="mb-8">
                    <div className="flex items-center justify-between mb-4">
                      <Button 
                        variant="ghost" 
                        onClick={() => {
                          setSelectedExternalFolder(null);
                          setExternalImages([]);
                        }}
                        className="text-gold hover:text-gold/80"
                      >
                        <ArrowLeft className="h-4 w-4 mr-2" /> Volver
                      </Button>
                      <h3 className="text-lg font-semibold text-white">
                        {externalFolders.find(f => f.id === selectedExternalFolder)?.nombre}
                      </h3>
                    </div>
                    
                    {loadingExternalImages ? (
                      <div className="flex justify-center py-20">
                        <Loader2 className="h-8 w-8 animate-spin text-gold" />
                      </div>
                    ) : (
                      <div className="photo-grid">
                        {externalImages.map((image, index) => (
                          <div 
                            key={image.nombre} 
                            className="photo-item card-mk group cursor-pointer"
                            onClick={() => openLightbox(
                              `${BACKEND_URL}${image.url}`, 
                              image.titulo,
                              externalImages.map(img => ({ url: `${BACKEND_URL}${img.url}`, title: img.titulo })),
                              index
                            )}
                          >
                            <img
                              src={`${BACKEND_URL}${image.url}`}
                              alt={image.titulo}
                              className="w-full h-full object-cover"
                            />
                            <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                              <ZoomIn className="h-6 w-6 text-white drop-shadow-lg" />
                            </div>
                            <div className="photo-overlay bg-gradient-to-t from-black to-transparent p-4">
                              <p className="text-white font-medium truncate">{image.titulo}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Fotos sin carpeta */}
                {!selectedExternalFolder && (
                <div>
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                    <Image className="h-5 w-5 text-gold" /> Fotos
                  </h3>
                </div>
                )}
              </div>
            )}

            {loading ? (
              <div className="flex justify-center py-20">
                <div className="loading-spinner"></div>
              </div>
            ) : !selectedExternalFolder && (
              <div className="photo-grid">
                {getFilteredPhotos().map((photo, index) => {
                  const filteredPhotos = getFilteredPhotos();
                  const imagesArray = filteredPhotos.map(p => ({ url: `${BACKEND_URL}${p.url}`, title: p.titulo }));
                  return (
                  <div key={photo.id} className="photo-item card-mk group relative">
                    <div 
                      className="w-full h-full cursor-pointer"
                      onClick={() => openLightbox(`${BACKEND_URL}${photo.url}`, photo.titulo, imagesArray, index)}
                    >
                      <img
                        src={`${BACKEND_URL}${photo.url}`}
                        alt={photo.titulo || 'Foto'}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                        <ZoomIn className="h-6 w-6 text-white drop-shadow-lg" />
                      </div>
                      <div className="photo-overlay bg-gradient-to-t from-black to-transparent p-4 pointer-events-none">
                        {photo.titulo && <h3 className="text-white font-semibold mb-1">{photo.titulo}</h3>}
                        {photo.descripcion && <p className="text-gray-300 text-sm mb-2">{photo.descripcion}</p>}
                      </div>
                    </div>
                    {(user?.role === 'entrenador' || photo.user_id === user?.id) && (
                      <Button 
                        size="sm" 
                        variant="destructive" 
                        className="absolute bottom-2 right-2 z-10 opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={(e) => { e.stopPropagation(); handleDelete(photo.id); }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                  );
                })}
                {getFilteredPhotos().length === 0 && (
                  <div className="col-span-full text-center py-20 text-gray-500">
                    No hay fotos {selectedFolder ? 'en esta carpeta' : 'aún'}. ¡Sé el primero en subir una!
                  </div>
                )}
              </div>
            )}
          </TabsContent>

          {/* Tab de Galería MK */}
          <TabsContent value="mk">
            <div className="flex justify-between items-center mb-6">
              <div>
                <h2 className="text-2xl font-bold text-gold flex items-center gap-2">
                  <Users className="h-6 w-6" /> Galería MK
                </h2>
                <p className="text-gray-400 text-sm">Fotos del equipo Mallorca Klan</p>
              </div>
              {user?.role === 'entrenador' && (
                <Dialog open={mkDialogOpen} onOpenChange={(open) => {
                  setMkDialogOpen(open);
                  if (!open) resetMkForm();
                }}>
                  <DialogTrigger asChild>
                    <Button className="btn-primary-mk">
                      <Plus className="mr-2 h-4 w-4" /> Subir Foto MK
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-gray-900 text-white border-gray-700">
                    <DialogHeader>
                      <DialogTitle className="text-gold">Subir Foto a Galería MK</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleMkSubmit} className="space-y-4">
                      <div className="space-y-2">
                        <Label>Seleccionar Fotos</Label>
                        <div className="flex gap-2">
                          <Button
                            type="button"
                            variant="outline"
                            className="flex-1 border-gray-600"
                            onClick={() => openGalleryPicker(handleMkFileChange, true)}
                          >
                            <ImageIcon className="mr-2 h-4 w-4" /> Galería (múltiple)
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            className="flex-1 border-gray-600"
                            onClick={() => openCameraPicker(handleMkFileChange)}
                          >
                            <Camera className="mr-2 h-4 w-4" /> Cámara
                          </Button>
                        </div>
                      </div>
                      {mkPreviewUrls.length > 0 && (
                        <div className="space-y-2">
                          <Label>{mkPreviewUrls.length} foto{mkPreviewUrls.length > 1 ? 's' : ''} seleccionada{mkPreviewUrls.length > 1 ? 's' : ''}</Label>
                          <div className="grid grid-cols-3 gap-2 max-h-48 overflow-y-auto">
                            {mkPreviewUrls.map((url, idx) => (
                              <img key={idx} src={url} alt={`Preview ${idx + 1}`} className="w-full h-20 object-cover rounded" />
                            ))}
                          </div>
                        </div>
                      )}
                      {mkPreviewUrls.length === 1 && (
                        <>
                          <div className="space-y-2">
                            <Label htmlFor="mk_titulo">Título (opcional)</Label>
                            <Input
                              id="mk_titulo"
                              value={mkFormData.titulo}
                              onChange={(e) => setMkFormData({ ...mkFormData, titulo: e.target.value })}
                              className="bg-gray-800 border-gray-700 text-white"
                              placeholder="Entreno del sábado..."
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="mk_descripcion">Descripción (opcional)</Label>
                            <Textarea
                              id="mk_descripcion"
                              value={mkFormData.descripcion}
                              onChange={(e) => setMkFormData({ ...mkFormData, descripcion: e.target.value })}
                              className="bg-gray-800 border-gray-700 text-white"
                              rows={3}
                              placeholder="Descripción de la foto..."
                            />
                          </div>
                        </>
                      )}
                      {mkUploading && mkSelectedFiles.length > 1 && (
                        <div className="space-y-1">
                          <div className="w-full bg-gray-700 rounded-full h-2">
                            <div className="bg-gold h-2 rounded-full transition-all" style={{ width: `${mkUploadProgress}%` }}></div>
                          </div>
                          <p className="text-xs text-gray-400 text-center">Subiendo... {mkUploadProgress}%</p>
                        </div>
                      )}
                      <Button type="submit" className="w-full btn-primary-mk" disabled={mkUploading || mkSelectedFiles.length === 0}>
                        {mkUploading ? 'Subiendo...' : `Subir ${mkSelectedFiles.length > 0 ? mkSelectedFiles.length : ''} Foto${mkSelectedFiles.length !== 1 ? 's' : ''}`}
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>
              )}
            </div>

            {loading ? (
              <div className="flex justify-center py-20">
                <div className="loading-spinner"></div>
              </div>
            ) : (
              <div className="photo-grid">
                {mkPhotos.map((photo, index) => {
                  const imagesArray = mkPhotos.map(p => ({ url: `${BACKEND_URL}${p.url}`, title: p.titulo }));
                  return (
                  <div key={photo.id} className="photo-item card-mk group relative">
                    <div 
                      className="w-full h-full cursor-pointer"
                      onClick={() => openLightbox(`${BACKEND_URL}${photo.url}`, photo.titulo, imagesArray, index)}
                    >
                      <img
                        src={`${BACKEND_URL}${photo.url}`}
                        alt={photo.titulo || 'Foto MK'}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                        <ZoomIn className="h-6 w-6 text-white drop-shadow-lg" />
                      </div>
                      <div className="photo-overlay bg-gradient-to-t from-black to-transparent p-4 pointer-events-none">
                        {photo.titulo && <h3 className="text-white font-semibold mb-1">{photo.titulo}</h3>}
                        {photo.descripcion && <p className="text-gray-300 text-sm mb-2">{photo.descripcion}</p>}
                      </div>
                    </div>
                    {user?.role === 'entrenador' && (
                      <Button 
                        size="sm" 
                        variant="destructive" 
                        className="absolute bottom-2 right-2 z-10 opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={(e) => { e.stopPropagation(); handleDeleteMk(photo.id); }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                  );
                })}
                {mkPhotos.length === 0 && (
                  <div className="col-span-full text-center py-20 text-gray-500">
                    <Users className="w-16 h-16 mx-auto mb-4 text-gray-600" />
                    <p>No hay fotos en la Galería MK aún</p>
                    {user?.role === 'entrenador' && (
                      <p className="text-sm mt-2">Sube fotos del equipo y los entrenamientos</p>
                    )}
                  </div>
                )}
              </div>
            )}
          </TabsContent>

          {/* Tab de Rincón Estoico */}
          <TabsContent value="estoico">
            <div className="mb-6">
              <div className="flex justify-between items-center mb-4">
                <div>
                  <h2 className="text-2xl font-bold text-gold flex items-center gap-2">
                    <BookOpen className="h-6 w-6" /> Rincón Estoico
                  </h2>
                  <p className="text-gray-400 text-sm">Frases e imágenes de sabiduría estoica para motivarte</p>
                </div>
                {(
                    <Dialog open={quoteDialogOpen} onOpenChange={(open) => {
                      setQuoteDialogOpen(open);
                      if (!open) {
                        setQuoteFormData({ frase: '', autor: '' });
                        setQuoteImageFile(null);
                        setQuoteImagePreview(null);
                      }
                    }}>
                    <DialogTrigger asChild>
                      <Button className="btn-primary-mk">
                        <Plus className="mr-2 h-4 w-4" /> Añadir Frase
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="bg-gray-900 text-white border-gray-700">
                      <DialogHeader>
                        <DialogTitle className="text-gold">Nueva Frase Estoica</DialogTitle>
                      </DialogHeader>
                      <form onSubmit={handleQuoteSubmit} className="space-y-4">
                        <div className="space-y-2">
                          <Label htmlFor="frase">Frase</Label>
                          <Textarea
                            id="frase"
                            value={quoteFormData.frase}
                            onChange={(e) => setQuoteFormData({ ...quoteFormData, frase: e.target.value })}
                            required
                            className="bg-gray-800 border-gray-700 text-white"
                            rows={4}
                            placeholder="El obstáculo es el camino..."
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="autor">Autor</Label>
                          <Input
                            id="autor"
                            value={quoteFormData.autor}
                            onChange={(e) => setQuoteFormData({ ...quoteFormData, autor: e.target.value })}
                            required
                            className="bg-gray-800 border-gray-700 text-white"
                            placeholder="Marco Aurelio"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Imagen de Fondo (opcional)</Label>
                          <div className="flex gap-2">
                            <Button
                              type="button"
                              variant="outline"
                              className="flex-1 border-gray-600"
                              onClick={() => openGalleryPicker(handleQuoteImageChange)}
                            >
                              <ImageIcon className="mr-2 h-4 w-4" /> Galería
                            </Button>
                            <Button
                              type="button"
                              variant="outline"
                              className="flex-1 border-gray-600"
                              onClick={() => openCameraPicker(handleQuoteImageChange)}
                            >
                              <Camera className="mr-2 h-4 w-4" /> Cámara
                            </Button>
                          </div>
                          {quoteImagePreview && (
                            <img src={quoteImagePreview} alt="Preview" className="w-full h-32 object-cover rounded mt-2" />
                          )}
                        </div>
                        <Button type="submit" className="w-full btn-primary-mk" disabled={uploadingQuote}>
                          {uploadingQuote ? 'Subiendo...' : 'Añadir Frase'}
                        </Button>
                      </form>
                    </DialogContent>
                  </Dialog>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {stoicQuotes.map((quote) => (
                <Card 
                  key={quote.id} 
                  className="card-mk stoic-quote-card overflow-hidden group relative"
                  style={{
                    backgroundImage: quote.imagen_url ? `url(${quote.imagen_url})` : 'none',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center'
                  }}
                >
                  <div className={`${quote.imagen_url ? 'bg-black/70' : 'bg-gradient-to-br from-gray-800 to-gray-900'} p-6 h-full`}>
                    <CardContent className="p-0 flex flex-col justify-between h-full min-h-[200px]">
                      <div>
                        <Quote className="stoic-quote-icon h-8 w-8 text-gold mb-4 opacity-50" />
                        <p className="stoic-quote-text text-white text-lg leading-relaxed italic mb-4">
                          &ldquo;{quote.frase}&rdquo;
                        </p>
                      </div>
                      <div className="flex justify-between items-end">
                        <div>
                          <p className="stoic-quote-author text-gold font-semibold">— {quote.autor}</p>
                          {quote.creado_por_nombre && (
                            <p className="text-gray-500 text-[11px] mt-0.5" data-testid={`quote-contributor-${quote.id}`}>
                              Aportada por {quote.creado_por_nombre}
                            </p>
                          )}
                        </div>
                        {(user?.role === 'entrenador' || quote.creado_por === user?.id) && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDeleteQuote(quote.id)}
                            className="text-red-400 hover:text-red-300 hover:bg-red-900/30 opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </div>
                </Card>
              ))}

              {stoicQuotes.length === 0 && (
                <div className="col-span-full">
                  <Card className="card-mk border-gray-800">
                    <CardContent className="p-12 text-center">
                      <BookOpen className="w-16 h-16 mx-auto mb-4 text-gray-600" />
                      <p className="text-gray-400">No hay frases estoicas aún</p>
                      <p className="text-gray-500 text-sm mt-2">
                        Añade frases de Marco Aurelio, Séneca, Epicteto...
                      </p>
                    </CardContent>
                  </Card>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Tab de Buenas Practicas / Normas */}
          <TabsContent value="normas">
            <BuenasPracticasTab isEntrenador={user?.role === 'entrenador'} />
          </TabsContent>
        </Tabs>
      </div>

      {/* Lightbox Modal */}
      {lightboxOpen && lightboxImage && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-sm"
          onClick={closeLightbox}
          data-testid="lightbox-modal"
        >
          <div className="relative w-full h-full flex items-center justify-center p-4">
            {/* Botón cerrar */}
            <button
              onClick={closeLightbox}
              className="absolute top-4 right-4 z-50 p-2 rounded-full bg-black/50 hover:bg-black/70 text-white transition-colors"
              data-testid="lightbox-close"
            >
              <X className="h-6 w-6" />
            </button>

            {/* Botón descargar */}
            <button
              onClick={(e) => { e.stopPropagation(); downloadImage(); }}
              className="absolute top-4 left-4 z-50 p-2 rounded-full bg-gold/80 hover:bg-gold text-gray-900 transition-colors flex items-center gap-2"
              data-testid="lightbox-download"
            >
              <Download className="h-5 w-5" />
              <span className="hidden sm:inline font-medium">Descargar</span>
            </button>

            {/* Botón anterior */}
            {lightboxImages.length > 1 && (
              <button
                onClick={(e) => { e.stopPropagation(); navigateLightbox('prev'); }}
                className="absolute left-4 top-1/2 -translate-y-1/2 z-50 p-3 rounded-full bg-black/50 hover:bg-gold/80 hover:text-gray-900 text-white transition-all"
                data-testid="lightbox-prev"
              >
                <ChevronLeft className="h-8 w-8" />
              </button>
            )}

            {/* Botón siguiente */}
            {lightboxImages.length > 1 && (
              <button
                onClick={(e) => { e.stopPropagation(); navigateLightbox('next'); }}
                className="absolute right-4 top-1/2 -translate-y-1/2 z-50 p-3 rounded-full bg-black/50 hover:bg-gold/80 hover:text-gray-900 text-white transition-all"
                data-testid="lightbox-next"
              >
                <ChevronRight className="h-8 w-8" />
              </button>
            )}

            {/* Imagen */}
            <img
              src={lightboxImage.url}
              alt={lightboxImage.title}
              className="max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl"
              onClick={(e) => e.stopPropagation()}
              data-testid="lightbox-image"
            />

            {/* Contador y título */}
            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black/70 px-6 py-2 rounded-full flex items-center gap-3">
              {lightboxImages.length > 1 && (
                <span className="text-gold font-medium">{lightboxIndex + 1} / {lightboxImages.length}</span>
              )}
              {lightboxImage.title && lightboxImages.length > 1 && <span className="text-gray-500">|</span>}
              {lightboxImage.title && (
                <p className="text-white text-center font-medium">{lightboxImage.title}</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Galeria;
